/* Use-case landing pages.
   ---------------------------------------------------------------------------
   One entry per indexable product page. These exist to answer a specific
   search ("youtube to shorts", "add subtitles to video") on a page that is
   about that job and nothing else, so the copy here has to be genuinely
   different per page rather than the home page with the heading swapped --
   near-duplicate pages built around keyword variants are doorway pages and
   get treated as such.

   Voice: short form is the point, not a compromise. These pages should sound
   like they were written by someone who actually edits and watches short-form
   video. Clear, specific, confident. Short sentences where they help, longer
   prose where an idea needs room. Avoid slogans for their own sake.

   Titles and descriptions are NOT here: they live in seo/siteMetadata.json,
   because the prerenderer and the sitemap read that file directly and cannot
   import TypeScript. useCaseContent.test.ts holds the two lists together. */

import { INSTAGRAM_PUBLISHING_LAUNCHED, SOCIAL_PUBLISHING_LAUNCHED, TWITCH_IMPORTS_LAUNCHED } from '../../config';

/* Hidden until the platforms approve publishing; the copy stays for launch. */
const whenPublishing = (text: string) => (SOCIAL_PUBLISHING_LAUNCHED ? text : '');
const INSTAGRAM_LAUNCHED = SOCIAL_PUBLISHING_LAUNCHED && INSTAGRAM_PUBLISHING_LAUNCHED;
const whenInstagram = (text: string) => (INSTAGRAM_LAUNCHED ? text : '');
const SCHEDULING_PAGE_SLUG = 'schedule-social-videos';
const whenTwitch = (text: string) => (TWITCH_IMPORTS_LAUNCHED ? text : '');
const TWITCH_PAGE_SLUG = 'twitch-to-tiktok';

export type UseCaseStep = { title: string; body: string };
export type UseCaseFaq = { question: string; answer: string };
/* width and height are CSS pixels; the files are captured at 2x. A shot with
   a video plays it as a muted loop and uses src as the poster. */
export type UseCaseShot = { src: string; alt: string; width: number; height: number; video?: string };
export type UseCaseLink = { href: string; label: string; blurb: string };
/* A side-by-side for pages that name another tool. Every competitor cell is a
   checked public fact, and `note` says where and when it was checked. */
export type UseCaseComparison = {
  heading: string;
  competitor: string;
  rows: { label: string; imabird: string; competitor: string }[];
  note: string;
};

export type UseCaseContent = {
  slug: string;
  eyebrow: string;
  /* Anchor text for links pointing here. Deliberately not the eyebrow: that
     is a chip in the hero and reads fine as "Subtitles", while a link wants
     to say what is on the other end of it. */
  linkLabel: string;
  /* One line for the card that links here from elsewhere on the site. */
  blurb: string;
  /* The h1 is split so the second half can carry the emphasis weight, the
     same way the home page sets its own headline. */
  heading: string;
  headingAccent: string;
  /* One plain sentence on what the page does, set under the h1 so readers and
     answer engines get it without the headline wordplay. **text** takes the
     display weight; ==text== gets the brand's pale-pink band. It never
     mentions the watermark. */
  summary: string;
  intro: string;
  stepsHeading: string;
  stepsNote: string;
  steps: UseCaseStep[];
  comparison?: UseCaseComparison;
  shot: UseCaseShot;
  shotHeading: string;
  shotBody: string;
  controlsHeading: string;
  controls: string[];
  controlsShot: UseCaseShot;
  notesHeading: string;
  notes: string[];
  faq: UseCaseFaq[];
  related: UseCaseLink[];
  ctaHeading: string;
};

const allUseCasePages: UseCaseContent[] = [
  {
    slug: 'ai-video-editor',
    eyebrow: 'Agentic editing',
    linkLabel: 'AI video editor',
    blurb: 'Describe the change and let the agent make it on the timeline you are already editing.',
    heading: 'Ask for the change.',
    headingAccent: 'See it on the timeline.',
    summary:
      'IMABIRD\'s AI video editor, in beta, lets you **ask for an edit in plain language**: the ==AI agent== changes the real timeline in checkpointed batches you can review, adjust or undo.',
    intro:
      'The useful version of an AI editor is not a prompt box that replaces your cut with a new mystery video. It is an editor you can direct. Tell IMABIRD what you want changed in plain language and the agent works on the same timeline you are already using, with the clips, captions and structure still visible and editable afterwards.',
    stepsHeading: 'Describe the edit, then inspect the result.',
    stepsNote: 'Beta',
    steps: [
      {
        title: 'Say what you want changed',
        body: 'Talk to the agent from the dashboard, a project or an open cut. Ask it to tighten cut 3, remove the first four seconds, rebuild the captions, find a specific moment or make another concrete change without having to translate the request into a sequence of controls first.',
      },
      {
        title: 'It looks through the material',
        body: 'The agent can use the transcript, audio and visual analysis of the media in scope to find the section you described. When it searches for a moment, the result comes back tied to actual timecodes rather than as a guess based only on the wording of the transcript.',
      },
      {
        title: 'It changes the real timeline',
        body: 'Timeline operations arrive as a revision-checked batch against the cut you have open. If you have changed the timeline in the meantime, the agent can detect that instead of blindly overwriting a newer version of your work.',
      },
      {
        title: 'Review, adjust or undo',
        body: 'The agent can inspect frames and play the result back as part of the workflow. You can keep the change, make another request, adjust it manually or undo the batch if it moved the edit in the wrong direction.',
      },
    ],
    shot: {
      src: '/product/editor.webp',
      alt: 'The IMABIRD timeline editor where the agent applies its changes',
      width: 1600,
      height: 1000,
    },
    shotHeading: 'The agent works on the timeline you already have.',
    shotBody:
      'There is no separate AI version of the project that has to be reconciled afterwards. When you ask for a change, it is applied through the same timeline operations you could have made by hand. That keeps the result legible. You can see what moved, continue editing normally and reverse the batch if it was not what you wanted.',
    controlsHeading: 'What the agent can reach',
    controls: [
      'The timeline: clips, tracks, audio, canvas and visual layout',
      'Captions, including rebuilding them from the audio when needed',
      'Your media library, searched by what is said and what appears on screen',
      ...(SOCIAL_PUBLISHING_LAUNCHED ? ['Publishing actions such as scheduling a finished clip or moving a queued post'] : []),
    ],
    controlsShot: {
      src: '/product/timeline.webp',
      alt: 'Captions, text and video clips on the IMABIRD timeline',
      width: 1072,
      height: 300,
    },
    notesHeading: 'Why use an agent inside an editor',
    notes: [
      'A lot of editing time is spent between noticing a problem and carrying out the fix. You watch the cut and immediately know that the opening drags, the captions need rebuilding or a section should disappear. The mechanical part is then finding the right control, making the change and repeating that sequence for every similar issue. A language interface is useful when it shortens that distance without hiding the timeline from you.',
      `The important constraint is trust. Letting software alter an edit is only useful when the changes can be inspected and reversed. Timeline writes are therefore checkpointed and revision-checked as batches. Actions that reach outside the editor${whenPublishing(', such as scheduling a post,')} require approval before they run.`,
      'The agent is not limited to transcript search. It can combine what was said with visual analysis of the footage, open frames to inspect a result and use the actual media when answering a request. That matters for edits involving something visible but never spoken aloud, or when the transcript alone is not enough to identify the right moment.',
      'It is still a beta, and the division of labour is deliberate. The agent is strongest on concrete, mechanical instructions: trim this, remove that, rebuild these captions, find the moment where this happens, add a specified change. Creative judgement and taste remain yours, which is why the timeline stays visible and the final decision stays with you.',
    ],
    faq: [
      {
        question: 'What can I actually ask it to do?',
        answer: `You can ask it to trim and tighten clips, remove or move sections, rebuild captions, adjust layout and canvas settings, find moments across your media${SOCIAL_PUBLISHING_LAUNCHED ? ', create projects or cuts, and schedule finished clips' : ' and create projects or cuts'}. Concrete instructions are where it is currently strongest.`,
      },
      {
        question: 'Does it edit the cut I have open?',
        answer: 'Yes. Changes are applied to the selected timeline as a revision-checked batch, so the agent works on your actual cut rather than creating a separate version you later have to merge back.',
      },
      {
        question: 'Can I undo what it did?',
        answer: 'Yes. Timeline changes are checkpointed as batches, so the agent\'s edits can be reversed if you do not want to keep them.',
      },
      {
        question: 'Can it find a specific moment for me?',
        answer: 'Yes. It can search both transcript and visual shot analysis across the media in scope and return timecoded matches, including moments that were visible but never explicitly named in the dialogue.',
      },
      ...(SOCIAL_PUBLISHING_LAUNCHED
        ? [{
            question: 'Will it post something without asking?',
            answer: 'No. Actions that write outside the editor, including scheduling, require your approval before they run.',
          }]
        : []),
      {
        question: 'Is it finished?',
        answer: 'No. It is in beta. It handles concrete editing tasks best today, and you should review the changes it proposes before keeping them.',
      },
    ],
    related: [
      { href: '/youtube-to-shorts', label: 'YouTube to Shorts', blurb: 'Generate the short-form clips that the agent can then help you refine.' },
      { href: '/schedule-social-videos', label: 'Schedule social videos', blurb: 'Use the same agent to place finished clips into the publishing queue.' },
      { href: '/add-subtitles-to-video', label: 'Add subtitles to video', blurb: 'Edit or rebuild captions as part of the same timeline workflow.' },
    ],
    ctaHeading: 'Ask for an edit.',
  },
  {
    slug: 'chatgpt-claude-plugin',
    eyebrow: 'Plugin',
    linkLabel: 'ChatGPT & Claude plugin',
    blurb: 'Let ChatGPT, Claude or Codex edit your Cuts while you watch.',
    heading: 'Your agent.',
    headingAccent: 'Our timeline.',
    summary:
      'The IMABIRD plugin lets **ChatGPT, Claude, Claude Code and Codex** edit your short-form video on the real IMABIRD timeline, cutting, captioning and reframing while you ==watch every step== in the editor.',
    intro:
      'You already have an AI you trust. ChatGPT, Claude, Claude Code, Codex. Add the IMABIRD plugin and it edits your short-form video on the real timeline. It cuts, captions, reframes and adds motion graphics with the same tools IMABIRD\'s own agent uses. You watch every step land in the editor. Your agent does the thinking on your own plan. IMABIRD does the video.',
    stepsHeading: 'Connect once. Then just ask.',
    stepsNote: 'Beta',
    steps: [
      {
        title: 'Connect your agent',
        body: 'In ChatGPT or Claude, add IMABIRD as a connector with https://www.imabird.productions/agent/mcp and sign in. In Claude Code or Codex, install the IMABIRD plugin with a key that has Agent MCP access from the API page. One Allow screen and you are done.',
      },
      {
        title: 'Hand it a Cut',
        body: 'Paste the editor link and say what you want. Tighten the open. Kill the dead air. Captions that land on every word. A title card in your brand colours. Plain language is enough. Your agent already knows the channel, the script and the joke you are going for.',
      },
      {
        title: 'Watch it edit',
        body: 'Every call shows up live in the editor\'s Agent panel, under your agent\'s name. It reads the timeline first, makes the change, then renders frames and looks at them before it calls the job done. No black box. No mystery export at the end.',
      },
      {
        title: 'Stay in charge',
        body: 'Keep editing while it works. If you save a change in between, its next edit is refused instead of written over yours. Exports, reframes and posts stop at an approval gate, and IMABIRD tells your agent to ask you first. Stop it any time from the editor.',
      },
    ],
    shot: {
      src: '/product/connect-app.webp',
      alt: 'The IMABIRD screen where you allow Claude to use your account',
      width: 1280,
      height: 800,
    },
    shotHeading: 'One Allow screen. No keys to copy.',
    shotBody:
      'ChatGPT and Claude sign in the way you would expect. IMABIRD shows which app is asking, which account it gets and where the answer goes. Allow it, and the app can edit for your account. Nothing else. It cannot touch your plan, your settings or the rest of the API. Change your mind and disconnect it from the API page. Every token it holds stops working on the spot.',
    controlsHeading: 'What your agent can reach',
    controls: [
      'The timeline: clips, tracks, audio, text and canvas, edited in revision-checked batches',
      'Captions, rebuilt from the audio and styled word by word',
      'Motion graphics and title cards, written straight onto the Cut',
      'Your media, searched by what is said and what is on screen',
      'Rendered frames, so it can check its own work',
      'Exports, reframes and posts, each behind an approval gate',
    ],
    controlsShot: {
      src: '/product/connected-apps.webp',
      alt: 'API keys with Agent MCP access and the connected apps list on the IMABIRD API page',
      width: 698,
      height: 488,
    },
    notesHeading: 'Why edit from the AI you already use',
    notes: [
      'Short form runs on speed. The idea for the next clip shows up while you are still finishing this one. If your agent already knows your channel, your scripts and your voice, it should be able to cut the video too. Not describe the edit. Make it.',
      'Most AI video tools want you to start over inside their own chat box. That is backwards. You already live in Claude or ChatGPT, and your agent already has the context. IMABIRD gives it hands: a real editor, real tools and a timeline you can open and fix yourself.',
      'Your agent thinks on your own plan, so there is no second AI subscription humming in the background. IMABIRD never runs its own model for a connected agent. It only charges for video work that costs something, like an export, the same as when you click the button yourself.',
      'Nothing happens behind your back. Each step is an action you can see, in order, in the editor. Your edits win over its edits. When a result looks wrong, stop it, undo it or tell it what to fix. The taste stays yours. The grind does not.',
    ],
    faq: [
      {
        question: 'Which agents work with IMABIRD?',
        answer: 'ChatGPT and Claude connect with a normal sign-in. Claude Code and Codex use the IMABIRD plugin with a key that has Agent MCP access. Other AI tools that take custom connectors work the same way.',
      },
      {
        question: 'How do I add it to Claude Code?',
        answer: 'Run claude plugin marketplace add LasseSander/imabird-plugin, then claude plugin install imabird@imabird. When it asks, paste a key with Agent MCP access from the API page.',
      },
      {
        question: 'Do I pay for the AI twice?',
        answer: 'No. Your agent runs on your own Claude, ChatGPT or Codex plan, and IMABIRD never calls its own model for it. You only pay IMABIRD for video work like exports, at the same price as doing it yourself.',
      },
      {
        question: 'Can it export or post without me?',
        answer: 'Exports, reframes and posts stop at an approval gate. IMABIRD tells your agent to ask you before it answers, and you see every gate in the editor.',
      },
      {
        question: 'What if I edit the Cut at the same time?',
        answer: 'Go ahead. The Cut is not locked between your agent\'s steps. If you save a change first, its next edit is refused and it reads the timeline again. Your work is never overwritten.',
      },
      {
        question: 'Is this an MCP server?',
        answer: 'Yes. Under the hood the plugin is an MCP server at https://www.imabird.productions/agent/mcp. Any MCP client can connect, with OAuth or with a key that has Agent MCP access.',
      },
      {
        question: 'How do I disconnect an app?',
        answer: 'Open the API page. Connected apps sit under your keys. Disconnect one and every token it holds stops working right away. Keys are revoked the same way.',
      },
    ],
    related: [
      { href: '/ai-video-editor', label: 'AI video editor', blurb: 'The same agent tools, built into IMABIRD and ready without any setup.' },
      { href: '/youtube-to-shorts', label: 'YouTube to Shorts', blurb: 'Make the clips first, then hand them to your agent to sharpen.' },
      { href: '/add-subtitles-to-video', label: 'Add subtitles to video', blurb: 'Captions your agent can rebuild and restyle word by word.' },
    ],
    ctaHeading: 'Hand your agent the timeline.',
  },
  {
    slug: 'ai-clipping-tool',
    eyebrow: 'Clips from a video',
    linkLabel: 'Clips from a video',
    blurb: 'The best moments of your podcast, vlog or stream, captioned and framed vertical.',
    heading: 'Your long video.',
    headingAccent: 'Its best moments.',
    summary:
      'Upload a podcast, vlog or stream and IMABIRD finds **up to 28 moments worth posting**, captions and reframes each one, and lets the ==AI agent== edit any clip you want changed.',
    intro:
      'You already made the long video. The hours go into finding the parts that work on their own, then cutting, captioning and reframing each one for a vertical feed. IMABIRD does that first pass. Upload an episode, a vlog or a stream and get a set of vertical clips back, each with a title and a line on why it was picked. Then open any of them in the editor and change it yourself, or ask the agent to.',
    stepsHeading: 'From one upload to a set of clips.',
    stepsNote: 'Up to 28 per run',
    steps: [
      {
        title: 'Upload the video',
        body: 'Upload a podcast episode, a vlog or a stream recording from your computer. A link to your own video on YouTube, Google Drive or Dropbox works too.',
      },
      {
        title: 'Set the shape once',
        body: 'Pick the length: 10 to 30 seconds, 30 to 60, or 60 to 90. Pick 9:16, 1:1 or 16:9. Choose a caption look and a reframing layout, including Streamer layouts that stack the facecam and the gameplay. Save it as a preset and every run starts there.',
      },
      {
        title: 'Let it find the moments',
        body: 'IMABIRD watches the whole video and pulls out the moments that stand on their own. A setup and its payoff. A reaction. A story with an ending. Each one comes back as its own clip with an AI score, so you know which to watch first.',
      },
      {
        title: 'Edit any clip, by hand or by asking',
        body: `Open a clip in the timeline. Trim the intro, extend the payoff or fix a caption yourself, or ask the AI agent to make the change. Export at up to 1080p, or 4K on Pro and Studio, with no watermark on any plan.${whenPublishing(' Post it yourself or line it up in the publishing calendar.')}`,
      },
    ],
    shot: {
      src: '/product/clip-card.webp',
      alt: 'A generated IMABIRD clip with burned-in captions, its title, its length and the reason it was picked',
      width: 612,
      height: 378,
    },
    shotHeading: 'Every clip arrives with a reason.',
    shotBody:
      'A clip comes back with a title, a length and one line on why it was picked. That line saves more time than it looks. You can read ten reasons in the time it takes to watch one clip. You know straight away which moment is the payoff and which is filler. Rate the ones that land. Open the ones that need a trim. Bin the rest without a second thought.',
    controlsHeading: 'What you set once',
    controls: [
      'Clip length: 10 to 30 seconds, 30 to 60, or 60 to 90',
      'Aspect ratio: 9:16 for TikTok, Shorts and Reels, or 1:1 and 16:9',
      'Reframing layouts, including Streamer, Active Speaker and Multi-Person',
      'Captions: twenty fonts and more than a dozen word-by-word animations',
      'An optional AI text line on top of every clip',
    ],
    controlsShot: {
      src: '/product/cut-wizard.webp',
      alt: 'IMABIRD run settings for clip length, aspect ratio, captions and reframing',
      width: 672,
      height: 962,
    },
    notesHeading: 'What makes a clip worth posting',
    notes: [
      'The first second does the work. Nobody owes your clip a second chance. A clip that opens on a breath, a "so anyway" or half a thought is already losing. Start on the line, the reaction or the moment the energy turns. If the payoff needs ten seconds of setup, cut the setup to three. Viewers fill in the gaps faster than you think.',
      'Post steadily. One great clip a week will not grow an account, and ten weak ones will not either. A steady run of clips that each have one clear reason to exist works better. A ranked set from every episode makes that easy to keep up.',
      'Captions are part of the hit. Words landing on the beat of the voice hold the eye and keep the pace up. Big, few and on time beats a full sentence parked at the bottom of the frame. Pick a look that fits the channel and keep it. Then the clips read as one account, not a pile of uploads.',
      'Streams need their own framing. A gameplay clip with a facecam in the corner does not survive a plain centre crop. You lose the game or you lose the face. A stacked layout puts the face on top and the action underneath, so both make it to vertical. Podcasts are the opposite problem: two or three people in one wide shot. They want a different layout altogether.',
      'Fix it by asking. When a clip is almost right, say what is wrong: start on the laugh, cut the second sentence, make the captions bigger. The agent makes the change on the clip\'s timeline, and you can still adjust it by hand or undo it.',
    ],
    faq: [
      {
        question: 'What can I upload?',
        answer: 'A video file from your computer: a podcast episode, a vlog, a stream recording or any long video. A link to your own video on YouTube, Google Drive or Dropbox works too. Live streams need to end first, because IMABIRD clips recorded video.',
      },
      {
        question: 'How many clips do I get from one video?',
        answer: 'Up to ten from anything under half an hour. Longer videos earn more: up to 14 from an hour, 20 from two hours and 28 from four. IMABIRD only returns moments that hold up on their own, so a short or quiet source can come back with fewer.',
      },
      {
        question: 'Can the AI agent edit the clips?',
        answer: 'Yes. Open a clip and ask for the change in plain language. The agent edits the clip on its timeline, and you can keep the change, adjust it by hand or undo it.',
      },
      {
        question: 'Is there a watermark?',
        answer: 'No. Exports carry no IMABIRD watermark on any plan, the free one included.',
      },
      {
        question: 'Does it handle a facecam over gameplay?',
        answer: 'Yes. The Streamer layouts find the facecam and stack it above or below the action. You can still move the framing on any clip in the camera view.',
      },
      {
        question: 'How long can the source be?',
        answer: 'At least as long as one clip: 10 seconds with the 10–30s clip length. The longest source you can use depends on your plan.',
      },
      {
        question: 'Can I try it for free?',
        answer: 'Yes. The free plan needs no card, and captions and reframing are included.',
      },
    ],
    related: [
      { href: '/ai-video-editor', label: 'AI video editor', blurb: 'Describe the change and let the agent make it on the timeline you are already editing.' },
      { href: '/podcast-to-clips', label: 'Podcast to clips', blurb: 'Turn a full video episode into clips that still make sense to someone who never heard it.' },
      { href: '/reframe-video-9-16', label: 'Reframe video to 9:16', blurb: 'Turn horizontal footage into vertical video, keeping the part of the frame that matters.' },
    ],
    ctaHeading: 'Find the clips.',
  },
  {
    slug: 'twitch-to-tiktok',
    eyebrow: 'Twitch to TikTok',
    linkLabel: 'Twitch to TikTok',
    blurb: 'Turn a stream VOD into vertical clips with the facecam stacked on top.',
    heading: 'The stream is over.',
    headingAccent: 'The clips are not.',
    summary:
      'IMABIRD turns a Twitch VOD into **vertical clips for TikTok, Shorts and Reels**: paste the VOD link and it finds the moments, ==stacks your facecam over the gameplay== and adds captions.',
    intro:
      'A stream is hours of you live. Somewhere in there are the minutes people would actually share. The clutch play. The rage quit. The chat moment that broke everyone. Paste the VOD and IMABIRD goes through it for you. It finds the moments, fits your facecam and the gameplay into one vertical frame and adds captions. You get clips ready for TikTok, Shorts and Reels.',
    stepsHeading: 'VOD in. Vertical clips out.',
    stepsNote: 'Facecam + gameplay',
    steps: [
      {
        title: 'Grab the VOD',
        body: 'Copy the link to a past broadcast on Twitch and paste it in. Or connect your Twitch account and pick from your latest VODs. The stream has to be over, because IMABIRD clips recorded broadcasts, not live ones.',
      },
      {
        title: 'Pick a Streamer layout',
        body: 'Streamer - Top puts your camera above the gameplay. Streamer - Bottom puts it below. IMABIRD finds the facecam in the frame on its own, so there is no box to draw and no template to line up.',
      },
      {
        title: 'Let it pick the moments',
        body: 'The whole broadcast gets watched, not only the loud parts. A two-hour stream gives up to twenty clips per run at the length you chose: 10 to 30 seconds, 30 to 60, or 60 to 90. Each one comes back captioned and scored.',
      },
      {
        title: 'Fix, export, post',
        body: 'Open a clip in the timeline to tighten the build-up or let the reaction run a beat longer. Then export in 9:16 with no watermark and post it wherever your audience scrolls.',
      },
    ],
    shot: {
      src: '/product/reframe-modes.webp',
      alt: 'IMABIRD reframing layouts, including Streamer with the camera above the action',
      width: 248,
      height: 408,
    },
    shotHeading: 'Two things in the frame. Both make it.',
    shotBody:
      'Gameplay is wide. Your face sits in a corner. A vertical crop of the middle loses both: the face falls off the edge and the game shrinks to a sliver. The Streamer layouts split the vertical frame instead. Your camera gets its own band and the action gets the rest. Viewers see the play and the reaction at the same time. The reaction is half the clip.',
    controlsHeading: 'What streamers usually set',
    controls: [
      'Streamer - Top or Streamer - Bottom, depending on where your face should sit',
      'Clip length: short for a single play, longer for a whole story',
      'A caption look that matches the channel',
      '9:16 for TikTok, Shorts and Reels',
    ],
    controlsShot: {
      src: '/product/reframing-layouts.webp',
      alt: 'IMABIRD reframing options, including Streamer - Top and Streamer - Bottom',
      width: 580,
      height: 100,
    },
    notesHeading: 'What makes a stream clip travel',
    notes: [
      'The reaction is the content. A great play on its own is a gameplay clip. A great play with your face losing it is a moment. That is why the facecam deserves real space in the frame, not a thumbnail in the corner. People share the reaction as much as the play.',
      'Cut the dead air hard. Streams breathe. You wait in a queue, read chat, grab a drink. A clip cannot. Start a beat before the moment turns and get out right after the payoff. If it needs context, a caption or a line of text on top does it faster than thirty seconds of lead-in.',
      'Chat moments need the chat. If the clip is funny because of what chat said, the viewer has to see it or hear it. Read the message out loud on stream, or keep it on screen in the crop. Otherwise the joke lands for you and nobody else.',
      'Post while it is fresh. A stream clip is most interesting while people still remember the stream. Clipping the same night beats a perfect batch next week. That speed is the whole point of letting the first pass run on its own while you log off.',
    ],
    faq: [
      {
        question: 'Can I paste a Twitch clip link?',
        answer: 'Not yet. IMABIRD takes Twitch VOD links, the ones for a full past broadcast. Paste the VOD and it finds the moments for you.',
      },
      {
        question: 'Can I clip a stream that is still live?',
        answer: 'No. Wait until the broadcast ends and the VOD is up, then paste its link.',
      },
      {
        question: 'Does it work on other people\'s streams?',
        answer: 'It works on any public Twitch VOD link. Make sure you have the streamer\'s permission, or that you are clipping for a campaign that allows it.',
      },
      {
        question: 'Do I have to mark where my facecam is?',
        answer: 'No. The Streamer layouts find the camera overlay and give it its own band in the vertical frame. You can still adjust the framing on any clip in the camera view.',
      },
      {
        question: 'Are the clips ready for TikTok?',
        answer: 'Yes. They export in 9:16 with the captions burned in and no watermark, which is what TikTok, YouTube Shorts and Instagram Reels expect.',
      },
    ],
    related: [
      { href: '/ai-clipping-tool', label: 'Clips from a video', blurb: 'The same first pass for podcasts, interviews and any other long video.' },
      { href: '/reframe-video-9-16', label: 'Reframe video to 9:16', blurb: 'More on getting horizontal footage into a vertical frame.' },
      { href: '/add-subtitles-to-video', label: 'Add subtitles to video', blurb: 'Make the captions hit on the beat of the voice.' },
    ],
    ctaHeading: 'Clip your last stream.',
  },
  {
    slug: 'opus-clip-alternative',
    eyebrow: 'Opus Clip alternative',
    linkLabel: 'Opus Clip alternative',
    blurb: 'How IMABIRD compares when you want every clip to stay editable.',
    heading: 'Keep the clips.',
    headingAccent: 'Lose the watermark.',
    summary:
      'IMABIRD is an Opus Clip alternative that turns a long video into **up to 28 captioned vertical clips** and opens every one in a **full timeline editor** that the ==AI agent== can change when you ask.',
    intro:
      'Opus Clip made AI clipping normal: long video in, short clips out. IMABIRD does that job too. The difference is what comes after. Every clip opens in a full timeline editor. An AI agent edits that timeline when you ask. And exports never carry a watermark, the free plan included. If you want the first pass and the finishing in one place, this is the switch.',
    stepsHeading: 'Switching takes one video.',
    stepsNote: 'No card',
    steps: [
      {
        title: 'Bring the same source',
        body: `Paste the YouTube link you would have given Opus Clip. ${whenTwitch('Twitch VODs, ')}Google Drive and Dropbox links work too, or upload the file. There is nothing to download first.`,
      },
      {
        title: 'Match your settings',
        body: 'Set clip length, aspect ratio, captions and reframing, then save them as a preset. The reframing layouts cover talking heads, podcasts with two or three people, and streams with a facecam.',
      },
      {
        title: 'Compare the picks',
        body: 'You get up to twenty clips from a two-hour video, each with an AI score and a line on why it was picked. Run the same video through both tools and judge the moments side by side. That is the only comparison that really counts.',
      },
      {
        title: 'Finish without leaving',
        body: `Open any clip in the timeline to trim it, re-time a caption, move the crop or add text. Or ask the agent to do it for you. Export with no watermark and post it${whenPublishing(', or line it up in the publishing calendar')}.`,
      },
    ],
    comparison: {
      heading: 'Side by side.',
      competitor: 'Opus Clip',
      rows: [
        { label: 'Watermark on free exports', imabird: 'No', competitor: 'Yes' },
        { label: 'Links on the free plan', imabird: `YouTube, ${whenTwitch('Twitch VODs, ')}Google Drive, Dropbox`, competitor: 'YouTube' },
        ...(TWITCH_IMPORTS_LAUNCHED ? [{ label: 'Twitch VOD links', imabird: 'Every plan', competitor: 'Pro plan and up' }] : []),
        { label: 'Kick links', imabird: 'Not yet, upload the file', competitor: 'Supported' },
      ],
      note: 'Opus Clip details are from opus.pro/pricing and its help centre, checked on 5 October 2026. Plans change, so check there before you decide.',
    },
    shot: {
      src: '/product/export.webp',
      alt: 'The IMABIRD export panel with resolutions from 360p up to 4K',
      width: 320,
      height: 300,
    },
    shotHeading: 'Nothing stamped on your clip.',
    shotBody:
      'A watermark is the price of a lot of free plans. It brands your clip with somebody else\'s tool. IMABIRD does not put one on anything. Export at 360p for a quick check or at 1080p for the feed, and at 4K on Pro and Studio. What you post is your clip and nobody else\'s logo.',
    controlsHeading: 'What you already know',
    controls: [
      'Long video in, up to 28 captioned vertical clips out',
      'An AI score on every clip, so you know where to start',
      'Clip lengths from 10 seconds up to 90',
      'Presets, so every run starts with your look',
    ],
    controlsShot: {
      src: '/product/timeline.webp',
      alt: 'Captions, text and video clips on the IMABIRD timeline',
      width: 1072,
      height: 300,
    },
    notesHeading: 'Picking a clipper that fits',
    notes: [
      'Judge the picks, not the feature list. Every AI clipper says it finds the best moments. The only test that counts is your own footage. Run one video you know well through each tool. See which one finds the moments you would have picked. Then count how many of the rest you would actually post.',
      'Count the minutes after the AI. A clipper that gets you most of the way and makes the last stretch painful is slower than it looks. Check what it takes to fix a caption, nudge a crop or start a clip two seconds earlier. That is the work you will do every single day.',
      'Read the free plan closely. Watermarks, short storage windows and locked editing are how free plans push you to upgrade. That is fair, but know it before you build a habit on one. IMABIRD\'s free plan has no watermark and opens every clip in the editor.',
      `Know where your sources live. ${TWITCH_IMPORTS_LAUNCHED ? 'If you mostly clip YouTube videos and Twitch VODs, IMABIRD takes both links directly.' : 'If you mostly clip YouTube videos, IMABIRD takes those links directly.'} If you clip Kick or Rumble streams, IMABIRD does not take those links yet. Download the VOD and upload the file instead.`,
    ],
    faq: [
      {
        question: 'Is IMABIRD free to try?',
        answer: 'Yes. The free plan needs no card and adds no watermark. Paid plans add more runs, and Pro and Studio add longer sources and 4K export.',
      },
      {
        question: 'Will my free exports carry a logo?',
        answer: 'No. Nothing is stamped on exports on any plan.',
      },
      {
        question: 'Does every clip get a score?',
        answer: 'Each clip shows an AI score out of 5 from the moment finder, plus a line on why it was picked. You can add your own star rating next to it.',
      },
      {
        question: 'Can I bring in a Kick stream?',
        answer: 'Not by link yet. Download the VOD and upload the file, and IMABIRD clips it like any other video.',
      },
      {
        question: 'Can the AI make the edits for me?',
        answer: 'Yes. The agent reads your footage and changes the real timeline when you ask: tighten a clip, rebuild captions, move the crop. Each batch is checkpointed, so you can undo it.',
      },
    ],
    related: [
      { href: '/ai-clipping-tool', label: 'Clips from a video', blurb: 'The best moments of your podcast, vlog or stream, captioned and framed vertical.' },
      { href: '/twitch-to-tiktok', label: 'Twitch to TikTok', blurb: 'Stream VODs with the facecam stacked over the gameplay.' },
      { href: '/ai-video-editor', label: 'AI video editor', blurb: 'What the agent can change on the timeline for you.' },
    ],
    ctaHeading: 'Try it on one video.',
  },
  {
    slug: 'klap-alternative',
    eyebrow: 'Klap alternative',
    linkLabel: 'Klap alternative',
    blurb: 'How IMABIRD compares when you pay for runs instead of counting clips.',
    heading: 'Count runs.',
    headingAccent: 'Not clips.',
    summary:
      'IMABIRD is a Klap alternative whose plans **count runs, not clips**: one run returns up to 28 captioned vertical clips, and every clip opens on a **full timeline** you can trim by hand or change by asking the ==AI agent==.',
    intro:
      'Klap and IMABIRD start the same way: a long video goes in and short vertical clips come out. The difference is how the work is counted and where it ends. IMABIRD plans count runs, not clips, so a run that returns twenty clips uses the same allowance as one that returns five, however many you keep. And every clip lands on a full timeline, where you can trim it by hand or ask the agent to change it for you.',
    stepsHeading: 'Same video, both tools.',
    stepsNote: 'Up to 28 clips a run',
    steps: [
      {
        title: 'Start a run',
        body: `Paste a YouTube link, ${whenTwitch('a Twitch VOD, ')}or a Google Drive or Dropbox link, or upload the file. One run covers the whole video, however many clips it returns.`,
      },
      {
        title: 'Set the batch once',
        body: 'Choose the clip length, the aspect ratio, a caption look and a reframing mode, then save them as a preset. Intelligent reframing reads each scene and frames a single speaker, a group, a webcam over gameplay or a shared screen differently.',
      },
      {
        title: 'Read the reasons first',
        body: 'Every clip comes back with a title, an AI score and one line on why it was picked. Skim the reasons, open the clips that sound right and drop the rest. The run is already counted, so dropping a clip costs nothing.',
      },
      {
        title: 'Finish on the timeline',
        body: `Open a clip to trim it, move the crop, fix a caption or add text. Or tell the agent what to change and check the result. Export with no watermark${whenPublishing(' or line the clip up in the publishing calendar')}.`,
      },
    ],
    comparison: {
      heading: 'Side by side.',
      competitor: 'Klap',
      rows: [
        { label: 'Watermark on free exports', imabird: 'No', competitor: 'No' },
        { label: 'Free use', imabird: 'One run every month, no card', competitor: 'One video to start, no card' },
        { label: 'Paid plans count', imabird: 'Runs, each returning up to 28 clips', competitor: 'Clips: 100, 300 or 1,000 a month' },
        { label: 'Links', imabird: `YouTube, ${whenTwitch('Twitch VODs, ')}Google Drive, Dropbox`, competitor: 'YouTube, Vimeo, Twitch, Google Drive' },
      ],
      note: 'Klap details are from klap.app, its pricing page and its AI clip generator page, checked on 8 October 2026. Plans change, so check there before you decide.',
    },
    shot: {
      src: '/product/cut-wizard.webp',
      alt: 'IMABIRD run settings for clip length, aspect ratio, captions and reframing',
      width: 672,
      height: 962,
    },
    shotHeading: 'One run. As many clips as the video holds.',
    shotBody:
      'A clip allowance makes the size of every batch part of the budget. IMABIRD counts the run instead. The video is watched once, every clip it finds is yours to keep or bin, and only the next video counts again. A long stream that yields twenty clips costs the same run as a short talk that yields five. That makes it cheap to be picky, and being picky is what makes an account worth following.',
    controlsHeading: 'What one run gives you',
    controls: [
      'Up to 10 clips from under half an hour, 20 from two hours and 28 from four',
      'An AI score and a reason on every clip',
      'Clip lengths from 10 seconds up to 90',
      'A full timeline editor and an agent for every clip',
    ],
    controlsShot: {
      src: '/product/clip-card.webp',
      alt: 'A generated IMABIRD clip with its title, length, score and the reason it was picked',
      width: 612,
      height: 378,
    },
    notesHeading: 'How to compare two clippers fairly',
    notes: [
      'Use one video for both, and pick one you know well. A stream you hosted or an episode you recorded works best, because you already know where the good moments are. Count how many of those each tool found, then count how many of the remaining clips you would actually post. A tool that finds your three best moments beats one that hands you thirty average ones.',
      'Price the month you really have, not the plan headline. Work out how many long videos you clip in a month and how many clips each one produces. A run allowance is spent per video; a clip allowance is spent per clip, so long sources that return big batches use it up faster. If a plan counts clips, check whether it counts every clip generated or only the ones you export.',
      'Time the last ten percent. Generating clips is fast in every tool. Fixing them is where the day goes: starting a clip two seconds earlier, correcting a name in the captions, moving the crop off the wrong person. Try each of those once in both tools. The one where they take seconds is the one you will still be using next month.',
      `Check where your sources live. Klap takes Vimeo links, and IMABIRD does not yet. IMABIRD takes Dropbox links${whenTwitch(' and Twitch VODs')} on every plan. If your footage sits somewhere neither tool reads, both take an uploaded file.`,
    ],
    faq: [
      {
        question: 'Do clips I delete count against my plan?',
        answer: 'No. Plans count runs, not clips. A run counts once however many clips it returns, though a source over three hours counts as more than one. Failed or cancelled runs do not count at all.',
      },
      {
        question: 'Can I paste a Vimeo link?',
        answer: 'Not yet. Download the video and upload the file, or put it in Google Drive or Dropbox and paste that link.',
      },
      {
        question: 'Can I change more than the captions and the crop?',
        answer: 'Yes. Every clip opens on a full timeline with tracks for video, audio, text and captions. You can trim, rearrange, add text or bring in other footage.',
      },
      {
        question: 'Why was a clip picked?',
        answer: 'Each clip shows one line from the moment finder on why it was chosen, next to its AI score. It is the fastest way to decide which clips to open first.',
      },
      {
        question: 'Can the agent fix a clip for me?',
        answer: 'Yes. Ask it in plain language to tighten the opening, rebuild the captions or move the crop. It edits the real timeline in checkpointed batches, so you can undo anything it did.',
      },
    ],
    related: [
      { href: '/opus-clip-alternative', label: 'Opus Clip alternative', blurb: 'How IMABIRD compares when you want every clip to stay editable.' },
      { href: '/ai-clipping-tool', label: 'Clips from a video', blurb: 'The best moments of your podcast, vlog or stream, captioned and framed vertical.' },
      { href: '/ai-video-editor', label: 'AI video editor', blurb: 'What the agent can change on the timeline for you.' },
    ],
    ctaHeading: 'Run one video.',
  },
  {
    slug: 'descript-alternative',
    eyebrow: 'Descript alternative',
    linkLabel: 'Descript alternative',
    blurb: 'For when the job is short-form clips, not editing the whole recording.',
    heading: 'Clips first.',
    headingAccent: 'Editor included.',
    summary:
      'IMABIRD is a Descript alternative built for short-form: it turns a finished recording into **up to 28 captioned vertical clips**, exports at **1080p on every plan**, and gives each clip a timeline editor and an ==AI agent== to finish it.',
    intro:
      'Descript is built around editing a whole recording: cut the transcript and the video follows. Clips are one feature among many. IMABIRD starts from the other end. Its main job is turning a long video into a batch of captioned vertical clips, and the timeline editor and agent are there to finish them. If short-form is most of what you make, start with the tool built around it.',
    stepsHeading: 'From finished episode to a batch of clips.',
    stepsNote: 'No watermark',
    steps: [
      {
        title: 'Bring the finished recording',
        body: 'Keep editing the full episode wherever you edit it now. When it is done, upload the export or paste a Google Drive, Dropbox or YouTube link.',
      },
      {
        title: 'Let the whole thing run',
        body: 'IMABIRD watches the full video and returns moments that work on their own, at the length you chose: 10 to 30 seconds, 30 to 60, or 60 to 90. A two-hour recording gives up to twenty.',
      },
      {
        title: 'Check the vertical crop',
        body: 'Each clip comes back reframed and captioned. Intelligent reframing handles a single speaker, two people in a wide shot and a shared screen differently, and the camera view shows the source beside the crop.',
      },
      {
        title: 'Finish and export',
        body: `Trim on the timeline, fix a caption word or ask the agent for the change. Export at 1080p on every plan, or 4K on Pro and Studio, with no watermark${whenPublishing(', or schedule the clip straight from the publishing calendar')}.`,
      },
    ],
    comparison: {
      heading: 'Side by side.',
      competitor: 'Descript',
      rows: [
        { label: 'Watermark on free exports', imabird: 'No', competitor: 'Yes' },
        { label: 'Free export resolution', imabird: '1080p', competitor: '720p' },
        { label: 'Paid plans priced', imabird: 'Per account', competitor: 'Per person' },
        { label: 'AI agent that edits for you', imabird: 'Yes, in beta', competitor: 'Yes, Underlord' },
      ],
      note: 'Descript details are from descript.com/pricing, checked on 8 October 2026. Plans change, so check there before you decide.',
    },
    shot: {
      src: '/product/timeline.webp',
      alt: 'Captions, text and video clips on the IMABIRD timeline',
      width: 1072,
      height: 300,
    },
    shotHeading: 'Every clip is a real edit.',
    shotBody:
      'A generated clip is a starting point, not a render. It arrives as clips, captions and text on a timeline, the same timeline you would have built by hand. Start it two seconds earlier, swap a caption word, move the crop on one shot or ask the agent to tighten the opening. Nothing has to be regenerated to change one detail.',
    controlsHeading: 'Built for the short-form job',
    controls: [
      'Up to 28 clips from one long video, each with an AI score',
      'Reframing layouts for speakers, groups, streams and shared screens',
      'Caption presets with word-by-word animations',
      '9:16, 1:1 and 16:9 from the same source',
    ],
    controlsShot: {
      src: '/product/captions.webp',
      alt: 'The IMABIRD caption editor with caption text and styling controls',
      width: 1149,
      height: 729,
    },
    notesHeading: 'One editor or two',
    notes: [
      'Editing a full episode and cutting short clips are different jobs. The long edit is about pacing over an hour: removing the false starts, levelling the audio, keeping the conversation flowing. Clipping is about finding thirty seconds that make sense to a stranger, then making them land in a vertical frame with captions that hit on the beat. A tool can do both, but it is usually designed around one.',
      'If short-form is a side output of your show, one editor that does a bit of everything may be enough. If clips are how most people find you, it pays to look at how much of the clipping is done for you. Count how many clips one run returns, how long it takes to fix a crop that followed the wrong person, and whether the clip is still editable after it is generated.',
      'You do not have to choose one tool for everything. Plenty of shows edit the full episode in one place and clip it in another. The handoff is a single export: the finished episode goes in, a batch of clips comes out, and the long edit stays exactly where it was.',
      'Look at what the free plan leaves on the video. A watermark or a lower export resolution is a fair way to sell an upgrade, but it means the free plan is only a trial run. IMABIRD exports free clips at 1080p with nothing stamped on them, so a clip made on the free plan can be posted.',
    ],
    faq: [
      {
        question: 'Can I edit by deleting words from the transcript?',
        answer: 'Not in the same way. In IMABIRD you trim on the timeline, correct caption words directly, or ask the agent in plain language to remove a section.',
      },
      {
        question: 'Do I have to move my whole edit over?',
        answer: 'No. Finish the full episode wherever you edit it now, then upload the export or paste a link to it. IMABIRD only needs the finished video.',
      },
      {
        question: 'What resolution can I export on the free plan?',
        answer: 'Up to 1080p, with no watermark. Pro and Studio add 4K export.',
      },
      {
        question: 'Are plans priced per seat?',
        answer: 'No. A plan belongs to one account, and its runs and credits are shared by everything that account does.',
      },
      {
        question: 'Is there an API?',
        answer: 'Yes. The developer API starts the same clipping runs from your own product or automation, so a published episode can turn into clips without anyone opening the app.',
      },
    ],
    related: [
      { href: '/podcast-to-clips', label: 'Podcast to clips', blurb: 'Clip a full episode when two or three people share one wide shot.' },
      { href: '/ai-video-editor', label: 'AI video editor', blurb: 'What the agent can change on the timeline for you.' },
      { href: '/add-subtitles-to-video', label: 'Add subtitles to video', blurb: 'See how caption text, timing and styling stay editable after generation.' },
    ],
    ctaHeading: 'Clip your next episode.',
  },
  {
    slug: 'webinar-to-clips',
    eyebrow: 'Webinar to clips',
    linkLabel: 'Webinar to clips',
    blurb: 'Turn a recorded webinar or talk into short clips with the slides kept whole.',
    heading: 'The webinar ran an hour.',
    headingAccent: 'The clips run a minute.',
    summary:
      'IMABIRD turns a recorded webinar into **captioned clips for Shorts, Reels, TikTok and LinkedIn**, framing speakers as people and ==keeping every slide whole== in the frame.',
    intro:
      'Most of a webinar is setup: introductions, housekeeping, waiting for people to join. The value sits in a handful of moments. A clear answer to a hard question. A demo that works. The one slide everybody screenshotted. IMABIRD finds those moments in the recording and turns each into a captioned clip. Speakers are framed as people and slides are kept whole, so the clip still makes sense on a phone.',
    stepsHeading: 'From recording to clips people watch.',
    stepsNote: 'Slides + speakers',
    steps: [
      {
        title: 'Bring in the recording',
        body: 'Upload the file your meeting tool saved, or put it in Google Drive or Dropbox and paste the link. If the webinar went out on YouTube, paste that link instead.',
      },
      {
        title: 'Choose the shape',
        body: 'Pick 9:16 for Shorts, Reels and TikTok, or keep 1:1 or 16:9 for LinkedIn and your own site. Answers and explanations usually want the longer clip lengths: 30 to 60 seconds, or 60 to 90.',
      },
      {
        title: 'Let it find the answers',
        body: 'IMABIRD watches the whole recording and keeps the moments that stand alone: a question and its answer, a demo from start to result, an explanation that does not lean on the previous twenty minutes.',
      },
      {
        title: 'Check the slides and names',
        body: 'Open the camera view to confirm each slide made it into the frame whole. Then read the captions for product names, company names and speaker names, the words a transcript most often gets wrong.',
      },
    ],
    shot: {
      src: '/product/dashboard.webp',
      alt: 'The IMABIRD dashboard, where a recording is uploaded or pasted as a YouTube, Google Drive or Dropbox link',
      width: 1440,
      height: 900,
    },
    shotHeading: 'A slide is not a face.',
    shotBody:
      'A plain vertical crop treats every frame the same way: find the middle and cut the rest. That is fine for a speaker and ruinous for a slide, which loses its title, its chart and half its words. Intelligent reframing looks at each scene first. A speaker gets framed as a person, a panel as a group, and a shared screen with slides, code or a chart is kept whole so the text stays in the picture.',
    controlsHeading: 'Worth setting for every webinar',
    controls: [
      'Intelligent reframing for scenes that switch between speakers and slides',
      'Longer clip lengths for answers and explanations',
      'A caption style that matches your brand, saved as a preset',
      '9:16 for short-form feeds, 1:1 or 16:9 for LinkedIn and your site',
    ],
    controlsShot: {
      src: '/product/compare.webp',
      alt: 'IMABIRD comparing the horizontal source video against the vertical output',
      width: 980,
      height: 522,
    },
    notesHeading: 'What makes a webinar clip worth posting',
    notes: [
      'Clip the answers, not the agenda. The planned talk is often the weakest material because it is built to be followed in order. The Q&A is where people ask what they actually wanted to know, and a good answer to a real question is a self-contained clip by design. Start on the question, so a stranger knows what is being answered.',
      'A slide on a phone is small. Even kept whole, a dense slide in a vertical frame is hard to read. The slides that make good clips are the ones with one chart, one number or one sentence. If a moment depends on a slide full of bullet points, let the speaker carry it and treat the slide as background.',
      'Record the speaker on their own if you can. A recording where the shared screen fills the frame and the speaker is a thumbnail in the corner gives any reframing tool little to work with. If your meeting tool can save the active speaker as a separate recording, keep that file. It makes better clips than the screen recording alone.',
      'Webinar clips do a different job from entertainment clips. They are proof that the session was worth attending. A clip that shows a speaker solving a real problem does more for next month\'s registrations than a highlight reel, and it keeps working long after the live date.',
    ],
    faq: [
      {
        question: 'Can I paste a Zoom recording link?',
        answer: 'Not yet. Download the recording and upload it, or put it in Google Drive or Dropbox and paste that link.',
      },
      {
        question: 'What happens to the slides when the clip goes vertical?',
        answer: 'Intelligent reframing recognises a shared screen and keeps the whole slide in the frame instead of cropping out the middle. You can change the framing of any shot in the camera view.',
      },
      {
        question: 'Does it work with a panel of several speakers?',
        answer: 'Yes. Scenes with several people are framed as a group, and the camera view lets you follow one speaker by hand when a moment belongs to them.',
      },
      {
        question: 'Can I keep the clips horizontal for LinkedIn?',
        answer: 'Yes. Aspect ratio is part of the preset, so you can make 1:1 or 16:9 clips from the same recording instead of 9:16.',
      },
      {
        question: 'Is there a length limit for a recording?',
        answer: 'A recording needs to be at least as long as one clip: 10 seconds with the 10–30s clip length. The longest recording you can use depends on your plan.',
      },
    ],
    related: [
      { href: '/reframe-video-9-16', label: 'Reframe video to 9:16', blurb: 'More on getting horizontal footage into a vertical frame.' },
      { href: '/podcast-to-clips', label: 'Podcast to clips', blurb: 'The same workflow for long conversations between two or three people.' },
      { href: '/add-subtitles-to-video', label: 'Add subtitles to video', blurb: 'Fix names and terms in the captions before export.' },
    ],
    ctaHeading: 'Clip your last webinar.',
  },
  {
    slug: 'youtube-to-shorts',
    eyebrow: 'YouTube to Shorts',
    linkLabel: 'YouTube to Shorts',
    blurb: 'Turn your YouTube video into a batch of vertical, captioned Shorts you can still edit.',
    heading: 'Upload your YouTube video.',
    headingAccent: 'Get the Shorts out.',
    summary:
      'Upload the video you posted on YouTube and IMABIRD turns it into a batch of **captioned 9:16 Shorts**, and every clip stays ==editable on the timeline==.',
    intro:
      'The useful parts of a long video are usually already there. The work is finding the moments that can stand on their own, cutting away the setup they no longer need, reframing them for vertical and getting the captions right. Upload the video and IMABIRD runs that first pass for you. You get a batch of editable Shorts rather than a folder of finished decisions.',
    stepsHeading: 'From upload to a batch of Shorts.',
    stepsNote: '9:16',
    steps: [
      {
        title: 'Upload the video',
        body: 'Use the original file you published, or download your video from YouTube Studio first. Drop it on the page or choose it, and the Cuts wizard takes it from there.',
      },
      {
        title: 'Let it find the moments',
        body: 'IMABIRD works through the full video and looks for sections that still make sense when they are removed from the original. Each selected moment becomes its own clip at the length you choose: 10 to 30 seconds, 30 to 60, or 60 to 90.',
      },
      {
        title: 'Check the crop and captions',
        body: 'Each clip comes back in the target aspect ratio with captions applied. Open the camera view when you want to see exactly what the vertical crop kept from the source, then move the framing on any shot where the important part ended up outside it.',
      },
      {
        title: 'Finish and publish',
        body: `Open any clip in the timeline to tighten it, extend it, remove a section or change the captions. When it is ready, export at the resolution you need${whenPublishing(` or send the finished clips to the publishing calendar and spread them across YouTube${whenInstagram(' and Instagram')}`)}.`,
      },
    ],
    shot: {
      src: '/product/camera-view.webp',
      alt: 'IMABIRD camera view showing the vertical crop chosen for a horizontal source',
      width: 622,
      height: 464,
    },
    shotHeading: 'A horizontal video does not become vertical by itself.',
    shotBody:
      'Turning 16:9 footage into 9:16 means discarding most of the width. A centre crop can work for a locked-off talking head, but it falls apart as soon as the subject moves, a second person enters the frame or something important sits near the edge. The camera view puts the full source beside the vertical result, so you can see what was lost and only fix the shots that actually need attention.',
    controlsHeading: 'What the preset decides',
    controls: [
      'Clip length: 10 to 30 seconds, 30 to 60, or 60 to 90',
      'Aspect ratio, with 9:16 as the default for Shorts, Reels and TikTok',
      'Reframing mode, including multi-face handling for wider conversations',
      'Caption preset, applied consistently across the generated batch',
    ],
    controlsShot: {
      src: '/product/cut-wizard.webp',
      alt: 'IMABIRD clip settings for length, aspect ratio, captions and reframing',
      width: 672,
      height: 962,
    },
    notesHeading: 'Why a Short needs its own edit',
    notes: [
      'A strong moment in a long video is not automatically a strong Short. In the original, a line may depend on a minute of setup, a question asked earlier or context the audience already has. Once you pull that moment out, all of that disappears. The best clips are the ones that still carry their own premise, development and payoff when a stranger sees them cold.',
      'That is why the first pass should look for complete moments rather than simply the loudest sentence or the highest-energy section. Sometimes the better clip starts five seconds earlier than the quote you originally noticed because those five seconds are what make the rest understandable.',
      'Captions matter for the same reason. On short-form video they are part of the pacing of the edit. The way a line breaks, when the next words appear and how much text sits on screen at once all change how quickly the clip feels. A good automatic pass gets you most of the way there; names, brands and unusual terms are the parts worth reading through before export.',
      'Treat the generated batch as a shortlist, not an obligation. A forty-minute upload may contain twelve plausible clips without containing twelve clips worth publishing. Keeping the strongest four is often better than filling the calendar with eight more that merely work.',
    ],
    faq: [
      {
        question: 'Can I use a video that is already on YouTube?',
        answer: 'Yes. Download your own video from YouTube Studio, or use the original file you uploaded there, and upload it to IMABIRD.',
      },
      {
        question: 'Can I change which moments were picked?',
        answer: 'Yes. The generated clips are starting points, not locked exports. Open any clip in the timeline to trim it, extend it, remove a section, rearrange it or discard it entirely.',
      },
      {
        question: 'What aspect ratio do the clips use?',
        answer: '9:16 is the default for short-form platforms, but aspect ratio is part of the cut preset. Change the preset and future batches follow the ratio you choose.',
      },
      ...(SOCIAL_PUBLISHING_LAUNCHED
        ? [{
            question: 'Can I publish straight to YouTube?',
            answer: `Yes. Connect YouTube${whenInstagram(' or Instagram')} from your profile, then schedule finished clips with a date, time and post caption from the publishing calendar.`,
          }]
        : []),
      {
        question: 'What does it cost to try?',
        answer: 'Signing up gives you 50 credits a month and does not require a card. Captions and reframing are included on the free tier.',
      },
    ],
    related: [
      { href: '/reframe-video-9-16', label: 'Reframe video to 9:16', blurb: 'See how horizontal footage is turned into a vertical frame and where manual adjustment still helps.' },
      { href: '/podcast-to-clips', label: 'Podcast to clips', blurb: 'Use the same workflow on longer multi-speaker episodes and interviews.' },
      { href: '/pricing', label: 'Pricing', blurb: 'See how credits, plan limits and the free tier work.' },
    ],
    ctaHeading: 'Turn an upload into Shorts.',
  },
  {
    slug: 'podcast-to-clips',
    eyebrow: 'Podcast to clips',
    linkLabel: 'Podcast to clips',
    blurb: 'Turn a full video episode into clips that still make sense to someone who never heard it.',
    heading: 'One episode.',
    headingAccent: 'More ways into it.',
    summary:
      'IMABIRD turns a full video podcast episode into **captioned vertical clips** that stand on their own, with ==multi-face reframing== for two or three people trading turns in a wide shot.',
    intro:
      'A full podcast asks for a large commitment from someone who does not know the show yet. A good clip asks for thirty seconds. IMABIRD works through the full video episode, finds moments that can stand on their own, cuts them into short-form pieces and reframes the conversation for vertical. You keep the episode, the generated clips and the edits together in one project.',
    stepsHeading: 'Turn the episode into a working batch.',
    stepsNote: 'Multi-speaker',
    steps: [
      {
        title: 'Bring the episode in',
        body: 'Upload the recording or paste a link. Google Drive and Dropbox are useful for unreleased episodes that are still sitting in a production folder, while a published YouTube episode can be pulled in directly from its URL.',
      },
      {
        title: 'Let the full episode run',
        body: 'IMABIRD looks for moments that resolve inside themselves: a question and its answer, a story with an ending, an argument with enough context to understand it, or a useful explanation that does not rely on the previous twenty minutes.',
      },
      {
        title: 'Check the conversation in vertical',
        body: 'Wide podcast shots are where reframing gets difficult. Multi-face reframing handles the common cases, but the camera view is there when the crop follows the wrong person, changes too aggressively or leaves something important outside the frame.',
      },
      {
        title: 'Clean the details and publish',
        body: `Read through names, companies and unusual terms in the captions, make any timeline changes you want, then export the finished clips${whenPublishing(' or spread them across the week from the publishing calendar')}.`,
      },
    ],
    shot: {
      src: '/product/project.webp',
      alt: 'An IMABIRD project with the clips generated from one long video',
      width: 1440,
      height: 538,
    },
    shotHeading: 'The source and every clip stay connected.',
    shotBody:
      'Podcast clipping rarely ends in one session. You publish the obvious moments, then come back later because a guest starts trending, a topic becomes relevant again or you simply need another post. Keeping the original episode beside the clips already made from it means you can return to the same source without rebuilding the project or hunting through old exports.',
    controlsHeading: 'Worth setting once per show',
    controls: [
      'A cut preset with the caption style and visual treatment you want the show to keep',
      'Clip length, which interviews and explanations often want toward the longer end',
      'Multi-face reframing for two or three people trading turns in a wide shot',
      'Aspect ratio for the platforms and placements you actually publish to',
    ],
    controlsShot: {
      src: '/product/dashboard.webp',
      alt: 'The IMABIRD dashboard with recent projects and clips',
      width: 1440,
      height: 900,
    },
    notesHeading: 'What makes a podcast clip work on its own',
    notes: [
      'The simplest test is whether a stranger can understand the clip without knowing what happened earlier in the episode. A brilliant line can still be a bad clip if it depends on a name, story or premise introduced twenty minutes before. When there is a choice, the slightly less dramatic moment that explains itself usually travels further than the stronger moment that needs context.',
      'Short clips also change the publishing rhythm of a show. A weekly episode gives you one large release. Five useful clips from that episode give you several smaller reasons to appear between releases, using material you already recorded. The economics only work when getting those clips out is quick enough that the editing does not become a second production cycle.',
      'Names deserve disproportionate attention in the caption pass. Podcasts are full of people, companies, products and technical terms that may have been introduced only once. Those are also the words viewers are most likely to search for or notice when they are wrong, so fixing them is worth more than polishing ordinary transcription.',
      'Framing should follow the shape of the conversation rather than mechanically chase whoever is speaking. If two people exchange short lines, a crop snapping left and right every two seconds can be more distracting than keeping both people visible. The camera view lets you make that judgement per clip instead of accepting one rule for the whole episode.',
    ],
    faq: [
      {
        question: 'Do I need a video podcast?',
        answer: 'Yes. IMABIRD edits video, so it needs a video recording of the episode rather than an audio-only file.',
      },
      {
        question: 'Can I use it on episodes I have not released yet?',
        answer: 'Yes. Upload the file directly or paste a Google Drive or Dropbox link to the recording before it is published.',
      },
      {
        question: 'How does it handle two or three speakers?',
        answer: 'Multi-face reframing is designed for that case. The camera view then shows the vertical crop against the source, so you can correct any shot where it followed the wrong person or used the wrong composition.',
      },
      {
        question: 'Can I keep every clip looking like the same show?',
        answer: 'Yes. Save a cut preset with your caption style, clip length, aspect ratio and reframing mode, then use that preset for each episode.',
      },
      {
        question: 'How long can the source episode be?',
        answer: 'Processing time depends on the length of the source and the options you choose. You can leave the run going and open completed clips from the dashboard as they become available.',
      },
    ],
    related: [
      { href: '/add-subtitles-to-video', label: 'Add subtitles to video', blurb: 'See how caption text, timing and styling stay editable after generation.' },
      { href: '/schedule-social-videos', label: 'Schedule social videos', blurb: 'Take a finished batch and spread it across the week or month.' },
      { href: '/ai-video-editor', label: 'AI video editor', blurb: 'Use the agent to make follow-up changes directly on the timeline.' },
    ],
    ctaHeading: 'Turn an episode into clips.',
  },
  {
    slug: 'add-subtitles-to-video',
    eyebrow: 'Subtitles',
    linkLabel: 'Add subtitles to video',
    blurb: 'Generate burned-in captions, then edit the words, timing and style before export.',
    heading: 'Captions are part',
    headingAccent: 'of the edit.',
    summary:
      'IMABIRD adds **captions to your video automatically** and renders them into the exported frame, while the ==wording, timing and style stay editable== on the timeline.',
    intro:
      'On short-form video, captions do more than repeat the dialogue. They control how much text the viewer sees at once, where the eye goes and how quickly the clip feels. IMABIRD generates and styles the captions as part of the edit, then leaves the wording and timing open so you can correct the parts that matter instead of accepting a finished transcript.',
    stepsHeading: 'Generate them, then make them fit the clip.',
    stepsNote: 'Editable',
    steps: [
      {
        title: 'Bring the video in',
        body: 'Upload a file or paste a supported link. Captions are generated as part of the editing pass, so there is no separate transcription workflow you have to run before you can start working on the clip.',
      },
      {
        title: 'Read the words that matter',
        body: 'Automatic transcription is usually strongest on ordinary speech and weakest on the words your audience notices most: names, brands, products, acronyms and specialist terms. Read those first instead of proofreading every sentence with the same level of attention.',
      },
      {
        title: 'Fix the wording and timing',
        body: 'Edit the caption text directly, then adjust when a line appears or disappears if it lands early, stays too long or covers the wrong beat. The goal is not only correct transcription; it is text that moves with the spoken delivery.',
      },
      {
        title: 'Save the style once',
        body: 'Caption styling lives in the cut preset. Once you have a treatment that fits the channel or show, reuse it across future clips instead of rebuilding the same visual decisions every time.',
      },
    ],
    shot: {
      src: '/product/captions.webp',
      alt: 'The IMABIRD caption editor with caption text and styling controls',
      width: 1149,
      height: 729,
    },
    shotHeading: 'The words and the timing both stay editable.',
    shotBody:
      'A transcript is only useful if it still behaves like part of the video. In IMABIRD, captions live on the timeline rather than arriving as untouchable output. You can correct a word, change where a line begins, shorten how long it stays on screen and check the result against the actual frame. That matters when a caption covers a face, misses the emphasis in a sentence or hangs around after the cut has already moved on.',
    controlsHeading: 'What you can change',
    controls: [
      'Caption text, including individual words the transcript got wrong',
      'Timing, for lines that arrive too early or stay on screen too long',
      'Caption style, saved into a preset and reused across future clips',
      'Aspect ratio, because line length and safe placement change with the frame',
    ],
    controlsShot: {
      src: '/product/caption-fonts.webp',
      alt: 'IMABIRD caption font picker',
      width: 360,
      height: 520,
    },
    notesHeading: 'Why the captions are rendered into the video',
    notes: [
      'There is a practical difference between a caption track and captions that are part of the picture. A separate track can be turned off, restyled or handled differently by each platform. Burned-in captions look the same after a re-upload or cross-post because the text is rendered directly into the frame.',
      'That permanence makes sense for short-form video because the captions are doing visual work. Their size, position, line breaks and timing are part of the composition of the clip. They can land with the speaker, emphasise a phrase and keep the viewer moving through the sentence rather than sitting beside the video as optional metadata.',
      'The trade-off is that styling becomes part of the export. That is why presets matter. Settling on a caption system once gives the channel a consistent look and removes a repetitive design decision from every future clip.',
      'Reading speed is usually a bigger problem than transcription accuracy. A full sentence may be perfectly correct and still be poor short-form captioning if it stays on screen too long or takes up half the frame. Shorter lines that turn over with the delivery are easier to read and make the clip feel more responsive.',
    ],
    faq: [
      {
        question: 'Can I correct a word the transcript got wrong?',
        answer: 'Yes. Caption text is editable in the timeline, which is especially useful for names, brands, acronyms and jargon that automatic transcription is more likely to miss.',
      },
      {
        question: 'Can I change how the captions look?',
        answer: 'Yes. Caption style is part of a cut preset, so you can choose a treatment once and reuse it across the clips generated with that preset.',
      },
      {
        question: 'Do the captions stay in sync if I trim the clip?',
        answer: 'Captions live on the timeline with the rest of the edit, so their timing can be adjusted alongside any trims or structural changes you make.',
      },
      {
        question: 'Are the captions part of the video file?',
        answer: 'Yes. They are rendered into the frame on export, so the viewer does not need to enable a separate caption track for them to appear.',
      },
      {
        question: 'Are captions included on the free tier?',
        answer: 'Yes. Signing up gives you 50 credits a month without requiring a card, and captions and reframing are both included.',
      },
    ],
    related: [
      { href: '/youtube-to-shorts', label: 'YouTube to Shorts', blurb: 'Generate captioned vertical clips directly from a long-form upload.' },
      { href: '/reframe-video-9-16', label: 'Reframe video to 9:16', blurb: 'Get the vertical composition right before placing captions inside it.' },
      { href: '/ai-video-editor', label: 'AI video editor', blurb: 'Ask the agent to rebuild or change captions directly on the timeline.' },
    ],
    ctaHeading: 'Add captions to a video.',
  },
  {
    slug: 'reframe-video-9-16',
    eyebrow: 'Reframing',
    linkLabel: 'Reframe video to 9:16',
    blurb: 'Turn horizontal footage into vertical video, keeping the part of the frame that matters.',
    heading: 'Horizontal footage.',
    headingAccent: 'A vertical decision.',
    summary:
      'IMABIRD reframes horizontal 16:9 video to **vertical 9:16 automatically**, following the action in each shot, and shows the ==source beside the crop== so you can fix any shot by hand.',
    intro:
      'Going from 16:9 to 9:16 is not just changing the dimensions of the file. Most of the original width has to disappear, which means the edit has to decide what stays visible on every shot. IMABIRD makes that first framing pass automatically, then shows you the source beside the result so you can correct the difficult shots instead of rebuilding the crop from scratch.',
    stepsHeading: 'Let the crop follow the footage.',
    stepsNote: '16:9 to 9:16',
    steps: [
      {
        title: 'Choose the target frame',
        body: 'Aspect ratio and reframing mode live in the cut preset, so the destination format is decided before the clips are generated rather than added as a last-minute export setting.',
      },
      {
        title: 'Let IMABIRD make the first pass',
        body: 'The reframing pass follows what is happening inside the shot instead of applying the same centre crop everywhere. That handles the straightforward shots automatically and leaves you with a much smaller set worth checking.',
      },
      {
        title: 'Compare source and output',
        body: 'Open the camera view to see the full horizontal frame beside the vertical result. The comparison makes it obvious when a second person, a chart, a hand gesture or important on-screen text has been cut away.',
      },
      {
        title: 'Adjust only the difficult shots',
        body: 'Move the framing where the automatic result chose the wrong area and leave the rest alone. You can correct individual shots without regenerating the entire clip.',
      },
    ],
    shot: {
      src: '/product/compare.webp',
      alt: 'IMABIRD comparing the horizontal source video against the vertical output',
      width: 980,
      height: 522,
    },
    shotHeading: 'The mistake is often outside the vertical frame.',
    shotBody:
      'A bad crop can look completely acceptable if you only watch the vertical output. The subject is visible, the image is sharp and nothing appears obviously broken. The problem becomes clear when you compare it with the original and notice the second person, the slide title, the product or the hand movement that disappeared. Source and output side by side turns reframing from guesswork into a quick visual check.',
    controlsHeading: 'What you control',
    controls: [
      'Aspect ratio for the platform or placement you are targeting',
      'Reframing mode for how the crop follows people and action through a shot',
      'Framing per shot, adjustable by hand in the camera view',
      'Layout when a single crop cannot reasonably contain everything that matters',
    ],
    controlsShot: {
      src: '/product/aspect-ratios.webp',
      alt: 'IMABIRD aspect ratio preview',
      width: 362,
      height: 531,
    },
    notesHeading: 'Where automatic reframing needs judgement',
    notes: [
      'Two people at opposite sides of a wide shot are the obvious hard case. A single vertical crop may not be able to hold both of them at once, so the right answer depends on the moment: follow the active speaker, hold a wider composition or use a layout that gives both people space. That is an editorial choice rather than a tracking problem.',
      'Screen recordings, slides and demonstrations create a different problem. A face has a clear centre of attention; a spreadsheet does not. The useful information may live in a corner of the source frame, and that location can change from shot to shot. Those sections are usually faster to place manually after the automatic pass has handled the rest.',
      'Existing on-screen text is another thing to check. Lower thirds, presentation labels, logos and text already burned into the 16:9 source often sit near the edges because they were designed for a wide frame. Those are exactly the parts a vertical crop removes first.',
      'Finally, the exported frame is not the same as the visible frame inside a social feed. Platforms cover part of the video with usernames, descriptions, buttons and progress controls. Keeping faces, captions and important graphics comfortably inside the centre of the composition gives them a better chance of surviving the interface around the video.',
    ],
    faq: [
      {
        question: 'Can I reframe to something other than 9:16?',
        answer: 'Yes. Aspect ratio is part of the cut preset, so you can choose a different target frame when the destination calls for it.',
      },
      {
        question: 'Does the crop move during the clip?',
        answer: 'It can. Reframing mode controls how the crop behaves through a shot, and the camera view lets you review the result and make manual adjustments where needed.',
      },
      {
        question: 'Can I fix just one bad shot?',
        answer: 'Yes. Framing can be adjusted per shot, so one poor crop does not force you to regenerate the rest of the clip.',
      },
      {
        question: 'Does it work on screen recordings?',
        answer: 'Yes, although screen content is more likely to need manual placement because the important information may sit anywhere in the source rather than following a face.',
      },
      {
        question: 'Can I check the crop before exporting?',
        answer: 'Yes. The editor can show the source against the output, which is the easiest way to see what the vertical frame removed.',
      },
    ],
    related: [
      { href: '/youtube-to-shorts', label: 'YouTube to Shorts', blurb: 'Turn an existing horizontal upload into a batch of vertical clips.' },
      { href: '/podcast-to-clips', label: 'Podcast to clips', blurb: 'See how reframing behaves when two or three speakers share a wide shot.' },
      { href: '/add-subtitles-to-video', label: 'Add subtitles to video', blurb: 'Place captions inside the frame after the vertical composition is settled.' },
    ],
    ctaHeading: 'Reframe the video.',
  },
  {
    slug: 'schedule-social-videos',
    eyebrow: 'Scheduling',
    linkLabel: 'Schedule social videos',
    blurb: 'Lay out finished clips across YouTube and Instagram without making each post its own task.',
    heading: 'Finish the batch.',
    headingAccent: 'Plan the month once.',
    summary:
      'IMABIRD schedules finished clips to **YouTube and Instagram from one publishing calendar** beside the editor, with a date, time and post caption for each, or lets the ==AI agent== place them with your approval.',
    intro:
      'Editing and publishing ask for different kinds of attention. Editing is close work on the clip in front of you. Publishing is deciding what should go out, where it belongs and when the next few weeks should be filled. IMABIRD keeps the calendar beside the editor so you can finish the creative work first, then arrange the batch while everything is still fresh in your head.',
    stepsHeading: 'From finished clip to a planned queue.',
    stepsNote: 'Calendar',
    steps: [
      {
        title: 'Connect the accounts',
        body: 'Connect YouTube and Instagram once from your profile. The destinations available to you depend on your plan, and you can still export manually whenever you do not want to publish through IMABIRD.',
      },
      {
        title: 'Choose a finished clip',
        body: 'Open publishing and select a clip that has finished rendering. Because the calendar lives inside the same product as the editor, the clips you just made are already there and ready to schedule.',
      },
      {
        title: 'Set the post details',
        body: 'Choose the destination, date, time and post caption. A batch of nine clips can be spread over several weeks in one sitting instead of becoming nine separate reminders to come back and post something later.',
      },
      {
        title: 'Or ask the agent to place it',
        body: 'Tell the agent which clip should go to which platform and when. It can prepare the export and create the scheduled post as one workflow, with your approval required before anything is written outside the editor.',
      },
    ],
    shot: {
      src: '/product/publishing.webp',
      alt: 'IMABIRD publishing with clips lined up for the next three days',
      width: 1265,
      height: 510,
    },
    shotHeading: 'A queue makes the plan visible.',
    shotBody:
      'A folder of exports tells you what you have made. A calendar tells you what is actually going out. Once the month is laid out, an empty week is obvious, three posts competing for the same day are obvious, and the same clip accidentally scheduled twice is obvious. Most of those problems can be fixed by moving something rather than going back into the editor to make more content.',
    controlsHeading: 'Set per post',
    controls: [
      'Destination: YouTube or Instagram',
      'Date and time, chosen per clip rather than for the whole batch',
      'The post caption, separate from the captions rendered inside the video',
      'Platform-specific details such as privacy, tags and subscriber notifications',
    ],
    controlsShot: {
      src: '/product/schedule.webp',
      alt: 'IMABIRD scheduling a clip to YouTube and Instagram',
      width: 900,
      height: 682,
    },
    notesHeading: 'Why scheduling belongs beside the editor',
    notes: [
      'Editing and posting want different states of mind. Editing is close work on one clip: the cut, the captions, the timing, the frame. Posting is broader. It is deciding what should go out this week, what can wait until next week and how a batch fits across the month. Mixing those decisions into the edit means attention gets divided just when the creative work needs it most.',
      'Keeping a queue separates the two jobs. Finish the clips first, then look at the batch as a whole. Once the month is visible, you can see where one week is overloaded, another is empty or two similar posts are sitting next to each other. Fixing the plan becomes a scheduling decision rather than another editing session.',
      'The post caption is its own small piece of work. It sits beside the video rather than inside it, it can help the platform understand what the post is about, and the same clip may need different wording on YouTube and Instagram. Writing those captions while the clips are still fresh in your head is usually much faster than reopening the video two weeks later and reconstructing what you wanted to say.',
      'Scheduling is optional. Export the finished clip at whatever resolution you need and post it yourself if that is how you prefer to work. The calendar is for the other situation: you just made nine clips in one session and do not want publishing them to become nine more jobs spread across the next three weeks. Arrange the batch once, write the captions, book the slots and move on.',
    ],
    faq: [
      {
        question: 'Which platforms can I publish to?',
        answer: 'YouTube and Instagram. Connect the accounts from your profile; which destinations are available depends on your plan.',
      },
      {
        question: 'Can the agent schedule for me?',
        answer: 'Yes. You can ask the agent to schedule a finished clip to a platform at a specific time. It can prepare the 1080p export from the original media and create the scheduled post in the same workflow, but it asks for your approval before the external action runs.',
      },
      {
        question: 'Do I have to schedule, or can I just export?',
        answer: 'You can always export instead. Choose the output resolution, download the finished clip and post it manually wherever you want.',
      },
      {
        question: 'Is the post caption the same as the captions in the video?',
        answer: 'No. The captions inside the frame are part of the edit. The post caption is the text that accompanies the upload on the platform and can be written separately for each scheduled post.',
      },
      {
        question: 'Can I change a post after queueing it?',
        answer: 'Yes. Scheduled posts remain visible in the publishing calendar, and a pending post can be moved to a different slot.',
      },
    ],
    related: [
      { href: '/podcast-to-clips', label: 'Podcast to clips', blurb: 'Turn a long episode into the batch you later spread across the calendar.' },
      { href: '/youtube-to-shorts', label: 'YouTube to Shorts', blurb: 'Generate a set of vertical clips from an upload you already have.' },
      { href: '/pricing', label: 'Pricing', blurb: 'See which connected accounts and publishing features are included with each plan.' },
    ],
    ctaHeading: 'Plan the next few weeks.',
  },
];

const hiddenSlugs = new Set([
  // The scheduling page promises Instagram throughout.
  ...(INSTAGRAM_LAUNCHED ? [] : [SCHEDULING_PAGE_SLUG]),
  ...(TWITCH_IMPORTS_LAUNCHED ? [] : [TWITCH_PAGE_SLUG]),
]);

export const useCasePages: UseCaseContent[] = allUseCasePages
  .filter((page) => !hiddenSlugs.has(page.slug))
  .map((page) => ({
    ...page,
    related: page.related.filter((link) => !hiddenSlugs.has(link.href.slice(1))),
  }));

export const useCaseSlugs: string[] = useCasePages.map((page) => page.slug);

export const findUseCase = (slug: string): UseCaseContent | undefined =>
  useCasePages.find((page) => page.slug === slug);
